<script>
    import {PATH_PREFIX} from '../../apiUrls.js';

    import {onMount, setContext} from 'svelte';
    import LeftRightLayout from '../Layout/LeftRightLayout.svelte';
    import TopBottomLayout from '../Layout/TopBottomLayout.svelte';
    import RoleList from './RoleSequenceList/RoleList.svelte';
    import {get, getSetting, setSetting, getCsrfToken} from '../../utils.js';
    import {calculateRoleTotalTime} from './RoleSequenceList/utils.js';
    import {sgettext} from '../../i18n.js';
    import ElementContextualEditTab from './ElementContextualEditTab.svelte';
    import ElementContent from './ElementView/ElementContent.svelte';
    import ActivityVersion from './ActivityVersion/ActivityVersion.svelte';
    import Messages from '../common/Messages.svelte';
    import {
        branchId,
        elementInstance,
        elementDetails,
        roles,
        rolesTotalTime,
        messages,
        activityVersion,
        location,
        editable,
        csrfMiddlewareToken,
        isActivityVersionHeaderSelected,
    } from './workspacesStore.js';
    import {elementTabs} from './tabStore.js';
    import ElementEditTab from './ElementEditTab.svelte';
    import ElementIssuesTab from '../Workspaces/ElementIssuesTab.svelte';
    import ElementViewTab from './ElementViewTab.svelte';
    import MaterialRiskTab from './MaterialRiskTab.svelte';
    import UsageTab from './UsageTab.svelte';
    import VersionsTab from './VersionsTab.svelte';
    import {toggleTabHide} from '../TabView/TabView.svelte';

    export let branch = 0;
    export let bottomRow = false;
    export let activityVersionId = null;
    export let getElementsApi = `${PATH_PREFIX}/sw/api/builder/activityversion_elements/${activityVersionId}/`;
    export let csrfToken = '';
    export let workspaceReadOnly = false;
    export let userPermissions = [];
    let valid = true;

    // Minimum width of left panel in pixels
    const minLeftWidth = 350;

    const workspaceLeftColumnWidthKey = 'workspace-left-column-width';
    let leftColumnWidth = getSetting(workspaceLeftColumnWidthKey) || 560;

    branchId.set(branch);
    csrfMiddlewareToken.set(csrfToken);

    let loadingSequenceList = false;
    let sequenceListEditable = true;
    let draftAvailable = false;
    let isSafetyElement = false;

    $: if ($elementDetails && $elementInstance && $elementInstance.id) {
        if ($elementInstance.versions) {
            draftAvailable = $editable && $elementInstance.versions.some(v => v.status === 'DRAFT');
        }

        isSafetyElement = $elementDetails.type === 'SAFETY';
    }

    elementTabs.set([
        {
            key: 'view',
            label: sgettext('$$VIEW_CURRENT$$'),
            active: true,
            content: ElementViewTab,
            hide: true,
            workspaceReadOnly
        },
        {
            key: 'edit',
            label: sgettext('$$EDIT_DRAFT$$'),
            content: ElementEditTab,
            hide: true,
            workspaceReadOnly
        },
        {
            key: 'contextual',
            label: sgettext('$$EDIT_CONTEXT$$'),
            content: ElementContextualEditTab,
            hide: true,
            workspaceReadOnly
        },
        {
            key: 'material_risk',
            label: sgettext('$$MATERIAL_RISK$$'),
            content: MaterialRiskTab,
            hide: true,
            workspaceReadOnly
        },
        {
            key: 'versions',
            label: sgettext('$$VERSIONS$$'),
            content: VersionsTab,
            hide: true,
            workspaceReadOnly
        },
        {
            key: 'usage',
            label: sgettext('$$RELATED_USE$$'),
            content: UsageTab,
            hide: true,
            workspaceReadOnly
        },
        {
            key: 'issues',
            label: sgettext('$$IMPROVEMENTS$$'),
            content: ElementIssuesTab,
            hide: true,
            workspaceReadOnly
        },

    ]);

    $: if (isSafetyElement && !workspaceReadOnly) {
        elementTabs.set(toggleTabHide('material_risk', !draftAvailable, $elementTabs));
    } else {
        elementTabs.set(toggleTabHide('material_risk', true, $elementTabs));
    }

    $: if ($elementDetails && $elementDetails.evId) {
        elementTabs.set(toggleTabHide('versions', false, $elementTabs));
        elementTabs.set(toggleTabHide('usage', false, $elementTabs));
        elementTabs.set(toggleTabHide('issues', false, $elementTabs));
        elementTabs.set(toggleTabHide('view', false, $elementTabs));
    } else {
        elementTabs.set(toggleTabHide('versions', true, $elementTabs));
        elementTabs.set(toggleTabHide('usage', true, $elementTabs));
        elementTabs.set(toggleTabHide('issues', true, $elementTabs));
    }

    $: if (workspaceReadOnly) {
        elementTabs.set(toggleTabHide('edit', true, $elementTabs));
        elementTabs.set(toggleTabHide('contextual', true, $elementTabs));
    } else if (sequenceListEditable && $elementDetails && $elementDetails.evId) {
        elementTabs.set(toggleTabHide('edit', false, $elementTabs));
        if ($elementInstance && $elementInstance.id) {
            elementTabs.set(toggleTabHide('contextual', false, $elementTabs));
        }
    }

    async function getElements() {
        loadingSequenceList = true;
        const response = await get(getElementsApi);

        if (response.ok) {
            const temp = await response.json();
            roles.set(temp.roles);
            location.set(temp.location);
            editable.set(temp.editable);

            sequenceListEditable = temp.editable;

            activityVersion.set({
                id: temp.id,
                name: temp.name,
                is_fiori_governed: temp.is_fiori_governed,
                template: temp.template,
                template_status: temp.template_status,
                manually_changed_instance_status: temp.manually_changed_instance_status,
                expired: temp.expired,
                start_time: temp.start_time,
                end_time: temp.end_time,
                isEditable: temp.editable,
                valid: temp.valid,
                activity_governance_type: temp.activity_governance_type,
                activity_id:  temp.activity_id,
                activity_type: temp.activity_type
            })
            rolesTotalTime.set(calculateRoleTotalTime($roles));
        }
        loadingSequenceList = false;
    }

    function handleLeftColumnResize(container, leftPanel) {
        const newWidth = parseInt(leftPanel.style.width, 10);
        leftColumnWidth = newWidth >= minLeftWidth ? newWidth : minLeftWidth;
        setSetting(workspaceLeftColumnWidthKey, leftColumnWidth);
    }

    onMount(async () => {
        if (activityVersionId) {
            getElements();
        }
    });

</script>

{#if $messages}
  <Messages messages={$messages} />
{/if}
<div class="base-layout">
  <TopBottomLayout {bottomRow}>
    <div id="topSlot" class="full-height" slot="top">
      <LeftRightLayout
        {leftColumnWidth}
        {minLeftWidth}
        padded={false}
        onResize={handleLeftColumnResize}
        scrollableRightY={false}
      >
        <div class="full-height" slot="left">
          <RoleList
            editable={sequenceListEditable && !workspaceReadOnly}
            loading={loadingSequenceList}
            frameWidth={leftColumnWidth}
            bind:valid
          />
        </div>
        <div class="full-height" slot="right">
          {#if $isActivityVersionHeaderSelected}
            <ActivityVersion
              {activityVersionId}
              branchId={branch}
              {leftColumnWidth}
              {userPermissions}
              bind:valid
            />
          {:else}
            <ElementContent {leftColumnWidth} />
          {/if}
        </div>
      </LeftRightLayout>
    </div>
    <div slot="bottom" />
  </TopBottomLayout>
</div>

<style>
  .base-layout {
    display: flex;
    height: 100%;
    padding: 4px;
    background-color: #9fa6ab;
  }

  .full-height {
    height: 100%;
  }

  :global(.fa) {
    cursor: pointer;
  }
</style>
